import * as React from "react";

import {
  authoringErrorMessage,
  fetchDocxBytes,
  runDocxUrl,
  useCancelRunMutation,
  useCreateRunMutation,
} from "../documentAuthoring.api";
import type { AuthoringRunFrame, AuthoringRunKind, AuthoringRunReport } from "../documentAuthoring.types";
import { isTemplateRunReport } from "../documentAuthoring.types";
import type { DocxEditorHandle } from "../editor/editorHandle";
import { useAuthoringRunSocket } from "./useAuthoringRunSocket";

export type CheckStatus = "idle" | "running" | "reviewed" | "failed" | "done";

export type AuthoringRunStatus =
  | "idle"
  | "starting"
  | "running"
  | "applying"
  | "repairing"
  | "loading"
  | "done"
  | "failed"
  | "cancelled";

export interface AuthoringRunState {
  status: AuthoringRunStatus;
  runId: string | null;
  kind: AuthoringRunKind | null;
  total: number | null;
  completed: number;
  checks: CheckStatus[];
  repairing: number | null;
  report: AuthoringRunReport | null;
  error: string | null;
}

export type AuthoringRunAction =
  | { type: "start"; kind: AuthoringRunKind; checkCount: number }
  | { type: "created"; runId: string }
  | { type: "frame"; frame: AuthoringRunFrame }
  | { type: "loaded" }
  | { type: "failed"; message: string }
  | { type: "cancelled" }
  | { type: "reset" };

export const INITIAL_RUN_STATE: AuthoringRunState = {
  status: "idle",
  runId: null,
  kind: null,
  total: null,
  completed: 0,
  checks: [],
  repairing: null,
  report: null,
  error: null,
};

const ACTIVE_STATUSES: ReadonlySet<AuthoringRunStatus> = new Set([
  "starting",
  "running",
  "applying",
  "repairing",
  "loading",
]);

export const isRunActive = (status: AuthoringRunStatus): boolean => ACTIVE_STATUSES.has(status);

const settle = (state: AuthoringRunState, index: number, status: CheckStatus): AuthoringRunState => {
  if (index < 0 || (state.total !== null && index >= state.total)) return state;
  const checks = state.checks.slice();
  while (checks.length <= index) checks.push("running");
  // The stream replays from 0 on reconnect; a check already settled is not counted twice.
  if (checks[index] !== "running" && checks[index] !== "idle") return state;
  checks[index] = status;
  return { ...state, checks, completed: state.completed + 1 };
};

const applyFrame = (state: AuthoringRunState, frame: AuthoringRunFrame): AuthoringRunState => {
  if (!isRunActive(state.status)) return state;
  switch (frame.type) {
    case "start":
      return {
        ...state,
        status: "running",
        total: frame.total,
        checks: Array.from({ length: frame.total }, (_, index) => state.checks[index] ?? "running"),
      };
    case "check_done":
      return settle({ ...state, status: "running" }, frame.index, "reviewed");
    case "check_failed":
      return settle({ ...state, status: "running" }, frame.index, "failed");
    case "applying":
      return { ...state, status: "applying" };
    case "repairing":
      return { ...state, status: "repairing", repairing: frame.count };
    case "done": {
      const checks = isTemplateRunReport(frame.report)
        ? frame.report.results.map(result => (result.error ? "failed" : "done") as CheckStatus)
        : state.checks;
      return { ...state, status: "loading", report: frame.report, checks };
    }
    case "error":
      return { ...state, status: "failed", error: frame.message };
  }
};

export const authoringRunReducer = (state: AuthoringRunState, action: AuthoringRunAction): AuthoringRunState => {
  switch (action.type) {
    case "start":
      return {
        ...INITIAL_RUN_STATE,
        status: "starting",
        kind: action.kind,
        total: action.checkCount || null,
        checks: Array.from({ length: action.checkCount }, () => "running" as CheckStatus),
      };
    case "created":
      return state.status === "starting" ? { ...state, runId: action.runId } : state;
    case "frame":
      return applyFrame(state, action.frame);
    case "loaded":
      return state.status === "loading" ? { ...state, status: "done" } : state;
    case "failed":
      return isRunActive(state.status)
        ? { ...state, status: "failed", error: action.message, checks: state.checks.map(() => "idle" as CheckStatus) }
        : state;
    case "cancelled":
      return isRunActive(state.status)
        ? { ...state, status: "cancelled", checks: state.checks.map(() => "idle" as CheckStatus) }
        : state;
    case "reset":
      return INITIAL_RUN_STATE;
  }
};

export const runBusyMessage = (state: AuthoringRunState, aiAuthor: string): string | null => {
  switch (state.status) {
    case "starting":
      return `Sending the document to ${aiAuthor}…`;
    case "running":
      return state.kind === "template" && state.total
        ? `${aiAuthor} is running the template: ${state.completed} of ${state.total} checks done.`
        : `${aiAuthor} is reviewing the document. This usually takes under a minute.`;
    case "applying":
      return "Adding the edits to the document…";
    case "repairing":
      return `Retrying ${state.repairing ?? 0} ${state.repairing === 1 ? "edit" : "edits"} that didn't fit…`;
    case "loading":
      return "Loading the redlined document…";
    default:
      return null;
  }
};

export interface StartRunInput {
  kind: AuthoringRunKind;
  instructions?: string;
  template_id?: string;
  /** Number of template checks, so the panel can show per-check rows before `start` arrives. */
  checkCount?: number;
}

interface Options {
  fseId: string;
  fileName: string;
  editor: DocxEditorHandle | null;
  /** Called after the result docx replaced the editor's document. */
  onLoaded?: () => void;
}

/**
 * Run flow: `editor.save()` → `POST /runs` → socket frames until `done` → fetch the result docx
 * → `editor.load()`. The reducer is pure so the state machine is unit-testable without an editor.
 */
export const useAuthoringRun = ({ fseId, fileName, editor, onLoaded }: Options) => {
  const [state, dispatch] = React.useReducer(authoringRunReducer, INITIAL_RUN_STATE);
  const [createRun] = useCreateRunMutation();
  const [cancelRun] = useCancelRunMutation();
  const editorRef = React.useRef(editor);
  const onLoadedRef = React.useRef(onLoaded);
  editorRef.current = editor;
  onLoadedRef.current = onLoaded;

  const socketRunId = state.runId && isRunActive(state.status) && state.status !== "loading" ? state.runId : null;
  useAuthoringRunSocket(socketRunId, frame => dispatch({ type: "frame", frame }));

  React.useEffect(() => {
    if (state.status !== "loading" || !state.runId) return;
    let cancelled = false;
    const runId = state.runId;
    fetchDocxBytes(runDocxUrl(runId))
      .then(async bytes => {
        if (cancelled) return;
        // `load` settles only once the editor has opened the document; `onLoaded`
        // reads the pending-change counts, which are still zero before that.
        await editorRef.current?.load(bytes);
        if (cancelled) return;
        dispatch({ type: "loaded" });
        onLoadedRef.current?.();
      })
      .catch(() => {
        if (!cancelled) dispatch({ type: "failed", message: "The edited document could not be loaded. Try again." });
      });
    return () => {
      cancelled = true;
    };
  }, [state.status, state.runId]);

  const start = React.useCallback(
    async ({ kind, instructions, template_id, checkCount = 0 }: StartRunInput) => {
      const handle = editorRef.current;
      if (!handle) return;
      dispatch({ type: "start", kind, checkCount });
      try {
        // Save first, so the run includes any edits made in the browser.
        const file = await handle.save();
        const { run_id } = await createRun({ fseId, fileName, file, kind, instructions, template_id }).unwrap();
        dispatch({ type: "created", runId: run_id });
      } catch (error) {
        dispatch({ type: "failed", message: authoringErrorMessage(error, "The review could not be started.") });
      }
    },
    [createRun, fseId, fileName],
  );

  const cancel = React.useCallback(() => {
    if (state.runId) void cancelRun(state.runId);
    dispatch({ type: "cancelled" });
  }, [cancelRun, state.runId]);

  const reset = React.useCallback(() => dispatch({ type: "reset" }), []);

  return { state, start, cancel, reset, busy: isRunActive(state.status) };
};
